import { useEffect, useRef, useState } from "react";
import { Mic, Square, Loader2 } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useFamily } from "../context/FamilyContext";

const pickMime = () => {
  if (typeof MediaRecorder === "undefined" || !MediaRecorder.isTypeSupported) return "";
  return ["audio/mp4", "audio/webm;codecs=opus", "audio/webm"].find((t) => MediaRecorder.isTypeSupported(t)) || "";
};

const extFor = (type) => (type.includes("mp4") ? "mp4" : type.includes("ogg") ? "ogg" : "webm");

export function VoiceInput({ value, onChange, placeholder, testid, maxSeconds = 60, rows = 3 }) {
  const { fid } = useFamily();
  const [state, setState] = useState("idle");
  const [secs, setSecs] = useState(0);
  const [note, setNote] = useState("");
  const rec = useRef(null);
  const timer = useRef(null);
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    return () => {
      clearInterval(timer.current);
      if (rec.current && rec.current.state !== "inactive") rec.current.stop();
    };
  }, []);

  const upload = async (blob) => {
    setState("busy");
    try {
      const form = new FormData();
      form.append("file", blob, `voice.${extFor(blob.type)}`);
      const r = await api.post(`/families/${fid}/transcribe`, form);
      const cur = valueRef.current;
      onChange(cur ? `${cur} ${r.data.text}` : r.data.text);
      setNote("");
    } catch (e) {
      setNote(errMsg(e, "We couldn't hear that clearly. Try again, or type instead."));
    }
    setState("idle");
  };

  const start = async () => {
    setNote("");
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setNote("Voice isn't available in this browser. No problem, you can type instead.");
      return;
    }
    let stream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (e) {
      setNote(
        e?.name === "NotAllowedError"
          ? "Microphone access is off. No problem, you can type instead. To use voice, allow the microphone in your browser settings."
          : "We couldn't reach your microphone. You can type instead.",
      );
      return;
    }
    const mime = pickMime();
    const r = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    const chunks = [];
    r.ondataavailable = (e) => e.data && e.data.size && chunks.push(e.data);
    r.onstop = () => {
      stream.getTracks().forEach((t) => t.stop());
      clearInterval(timer.current);
      upload(new Blob(chunks, { type: r.mimeType || mime || "audio/webm" }));
    };
    rec.current = r;
    r.start(1000);
    setSecs(0);
    setState("rec");
    timer.current = setInterval(() => {
      setSecs((s) => {
        if (s + 1 >= maxSeconds && rec.current?.state === "recording") rec.current.stop();
        return s + 1;
      });
    }, 1000);
  };

  const stop = () => rec.current?.state === "recording" && rec.current.stop();

  return (
    <div className="space-y-2">
      <div className="relative">
        <textarea
          data-testid={`${testid}-text`}
          className="hf-input min-h-[96px] pr-20"
          rows={rows}
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
        />
        <button
          type="button"
          data-testid={`${testid}-mic`}
          onClick={state === "rec" ? stop : start}
          disabled={state === "busy"}
          aria-label={state === "rec" ? "Stop recording" : "Record your voice"}
          className={`absolute bottom-3 right-3 flex h-14 w-14 items-center justify-center rounded-full text-white transition-transform active:scale-95 ${
            state === "rec" ? "hf-pulse bg-hf-coral" : "bg-hf-green"
          }`}
        >
          {state === "busy" ? <Loader2 className="h-6 w-6 animate-spin" /> : state === "rec" ? <Square className="h-5 w-5" /> : <Mic className="h-6 w-6" />}
        </button>
      </div>
      {state === "rec" && (
        <p data-testid={`${testid}-recording`} className="text-base font-medium text-hf-coraldark">
          Listening... {secs}s of {maxSeconds}s. Tap the square when you're done.
        </p>
      )}
      {state === "busy" && <p className="text-base text-hf-muted">Turning your voice into words...</p>}
      {note && (
        <p data-testid={`${testid}-mic-note`} className="rounded-xl bg-hf-coralsoft px-4 py-3 text-base text-hf-ink">
          {note}
        </p>
      )}
    </div>
  );
}
